import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Container, Form, Spinner, Row, Col } from "react-bootstrap";
import { Title } from "../components/common/Title";
import ItemPriceHistoryChart from "../components/market/ItemPriceHistoryChart";
import SoldPerMonthChart from "../components/market/SoldPerMonthChart";
import type { ItemStats } from "../types/market";
import "./MarketItemStats.css";

const formatNumber = (value: number) => value.toLocaleString();

export const MarketItemStats = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const nameParam = searchParams.get("name") || "";

  const [query, setQuery] = useState(nameParam);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [stats, setStats] = useState<ItemStats | null>(null);
  const [loading, setLoading] = useState(false);

  // Autocomplete suggestions, debounced - doesn't fire while a name is
  // already selected and matches the query exactly (i.e. right after
  // picking a suggestion), so the dropdown doesn't immediately reappear.
  useEffect(() => {
    if (!query || query === nameParam) {
      setSuggestions([]);
      return;
    }
    const API_HOST = process.env.REACT_APP_API;
    const timeout = setTimeout(() => {
      fetch(`${API_HOST}public/market/item-names?q=${encodeURIComponent(query)}`)
        .then(res => res.json())
        .then(setSuggestions)
        .catch(err => console.error("Error fetching item name suggestions:", err));
    }, 300);
    return () => clearTimeout(timeout);
  }, [query, nameParam]);

  useEffect(() => {
    if (!nameParam) {
      setStats(null);
      return;
    }
    const API_HOST = process.env.REACT_APP_API;
    setLoading(true);
    fetch(`${API_HOST}public/market/item-stats?name=${encodeURIComponent(nameParam)}`)
      .then(res => res.json())
      .then(setStats)
      .catch(err => console.error("Error fetching item stats:", err))
      .finally(() => setLoading(false));
  }, [nameParam]);

  const pickItem = (name: string) => {
    setQuery(name);
    setSuggestions([]);
    setSearchParams({ name });
  };

  return (
    <Container>
      <Title text="Item Price History" />

      <Form onSubmit={e => e.preventDefault()} className="position-relative mb-4">
        <Form.Group>
          <Form.Label className="text-light">Item name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Start typing an item name..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoComplete="off"
          />
        </Form.Group>
        {suggestions.length > 0 && (
          <div className="market-autocomplete-list">
            {suggestions.map(name => (
              <div
                key={name}
                className="market-autocomplete-item"
                onClick={() => pickItem(name)}
              >
                {name}
              </div>
            ))}
          </div>
        )}
      </Form>

      {loading && <Spinner animation="border" variant="warning" />}

      {!loading && nameParam && !stats && (
        <div className="text-light">No data found for "{nameParam}".</div>
      )}

      {!loading && stats && (
        <>
          <Row className="mb-4 text-light">
            <Col xs={4}>
              <div className="market-stat-card">
                <div className="market-stat-value">{stats.totalListings}</div>
                <div className="market-stat-label">Total Listings</div>
              </div>
            </Col>
            <Col xs={4}>
              <div className="market-stat-card">
                <div className="market-stat-value">{stats.totalSold}</div>
                <div className="market-stat-label">Sold</div>
              </div>
            </Col>
            <Col xs={4}>
              <div className="market-stat-card">
                <div className="market-stat-value">{stats.totalBids}</div>
                <div className="market-stat-label">Total Bids</div>
              </div>
            </Col>
          </Row>

          {stats.byCurrency.length === 0 ? (
            <div className="text-light">
              No sales recorded yet for this item.
            </div>
          ) : (
            stats.byCurrency.map(cs => {
              const history = stats.priceHistory.filter(
                h => h.currency === cs.currency
              );
              const soldPerMonth = stats.soldPerMonth.filter(
                s => s.currency === cs.currency
              );
              return (
                <div key={cs.currency} className="mb-5">
                  <h5 className="text-light">{cs.currency} sales</h5>
                  <Row className="mb-3 text-light">
                    <Col xs={4}>
                      <div className="market-stat-card">
                        <div className="market-stat-value">
                          {formatNumber(cs.average)}
                        </div>
                        <div className="market-stat-label">Average</div>
                      </div>
                    </Col>
                    <Col xs={4}>
                      <div className="market-stat-card">
                        <div className="market-stat-value">
                          {formatNumber(cs.min)}
                        </div>
                        <div className="market-stat-label">Min</div>
                      </div>
                    </Col>
                    <Col xs={4}>
                      <div className="market-stat-card">
                        <div className="market-stat-value">
                          {formatNumber(cs.max)}
                        </div>
                        <div className="market-stat-label">Max</div>
                      </div>
                    </Col>
                  </Row>
                  {history.length > 1 && (
                    <>
                      <h6 className="text-light">Price over time</h6>
                      <ItemPriceHistoryChart currency={cs.currency} history={history} />
                    </>
                  )}
                  {soldPerMonth.length > 1 && (
                    <>
                      <h6 className="text-light mt-4">Sold per month</h6>
                      <SoldPerMonthChart entries={soldPerMonth} />
                    </>
                  )}
                </div>
              );
            })
          )}
        </>
      )}
    </Container>
  );
};

export default MarketItemStats;
